import { Fragment, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "src/layouts/DashboardLayout";
import { useAuth } from "src/context";
import { StyledLink } from "src/components/common";
import { Button } from "@porchpass/ui";
import { formatPhoneNumber, addCountryCode, dealerStorage } from "src/utils";
import { Footer } from "./LoginPage/Footer";

const getOtpInputClassName = (showError: boolean, hasValue: boolean) => {
  const baseClassName =
    "relative z-[2] mb-4 box-border rounded-xl border-0 bg-[#f0f1f4] p-0.5 text-center text-[32px] font-normal text-[#31394a] outline-none placeholder:text-transparent md:h-20 md:w-[59px] md:text-[40px] h-[72px] w-[46px] focus:bg-[#fafafb] focus:shadow-[0_0_0_4px_rgba(20,83,245,0.12)] focus:outline-2 focus:outline-[#3265EA] focus:caret-[#3265EA]";

  if (!showError) {
    return baseClassName;
  }

  if (hasValue) {
    return `${baseClassName} bg-[#fafafb] shadow-[0_0_0_4px_#87252529] outline-2 outline-[#872525] caret-[#872525]`;
  }

  return `${baseClassName} bg-[#fafafb] shadow-[0_0_0_3px_#87252529] outline outline-[#872525] caret-[#872525]`;
};

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export default () => {
  const { dealerName } = useParams();
  const navigate = useNavigate();
  const authObject = useAuth();
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userPhone = dealerStorage.getItem("phone-for-sign-in") || "";

  useEffect(() => {
    if (!userPhone) {
      navigate(`/${dealerName}`);
    }
    /**
     * Ensures removal of the redirect to this page
     * if user navigates away, submits, or closes browser.
     * We don't want this redirect item stored indefinitely.
     */
    const handleUnload = () => {
      window.localStorage.removeItem("publicRedirect");
    };
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      handleUnload();
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (authObject.user) {
      navigate(`/${dealerName}/dashboard`, { replace: true });
    }
  }, [authObject.user]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const intervalId = setTimeout(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearTimeout(intervalId);
  }, [timeLeft]);

  const handleResend = () => {
    authObject.sendSMSCode(addCountryCode(userPhone), () => {
      setTimeLeft(120);
    });
  };

  const handleSubmit = (newOtp?: string[]) => {
    setIsLoading(false);
    const verificationCode = newOtp ? newOtp.join("") : otp.join("");

    const onSuccess = () => {
      setIsError(false);
      setIsLoading(false);
    };
    const onFailure = () => {
      setIsError(true);
      setIsLoading(false);
    };
    if (verificationCode.length === 6) {
      authObject.signInWithSMSCode(verificationCode, onSuccess, onFailure);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const newOtp = [...otp];
    newOtp[index] = e.target.value;
    setOtp(newOtp);

    // Clear error when user types a value
    if (e.target.value && isError) {
      setIsError(false);
    }

    // Move to the next input after entering a value
    if (e.target.value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    // Focus the previous input if backspace is pressed and the current input is empty
    if (e.key === "Backspace" && index > 0 && !otp[index]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6); // Get only first 6 characters

    if (/^\d{6}$/.test(pastedData)) {
      // Ensure it's exactly 6 digits
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      // Focus on last filled input
      inputRefs.current[otp.length - 1]?.focus();
      handleSubmit(newOtp);
    }
  };

  const isDisabled = () => {
    return !otp.every((digit) => digit !== "");
  };

  return (
    <DashboardLayout
      pageTitle="SMS Sent"
      backHandler={() => navigate(`/${dealerName}`)}
    >
      <div
        className="box-border fixed top-1/2 left-1/2 flex w-full max-w-[450px] grow flex-col items-center gap-0 px-5 text-center -translate-x-1/2 -translate-y-1/2"
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <div
          className="mt-12 mb-5 text-center text-[28px] font-medium"
          data-replay-safe
        >
          Check your phone for a code
        </div>
        <div className="mb-7 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-[3px] text-center text-sm font-light text-[#4a5466]">
          We&apos;ve sent a code to{" "}
          <div className="inline rounded-lg bg-[#e5ebfd] px-1.5 py-0.5 font-normal text-[#1453f5]">
            {formatPhoneNumber(userPhone)}
          </div>
        </div>
        <div className="flex items-center justify-center gap-1.5">
          {otp.map((digit, index) => (
            <Fragment key={index}>
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                className={getOtpInputClassName(isError, !!digit)}
                type="text"
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={() => setIsError(false)}
                onPaste={handlePaste}
                pattern="[0-9]"
                inputMode="numeric"
              />
              {index === 2 && <span className="text-2xl text-[#333]">-</span>}
            </Fragment>
          ))}
        </div>
        {isError ? (
          <span className="-mt-1 -mb-2.5 text-center text-sm text-[#b93c3c]">
            The code you entered is invalid.
          </span>
        ) : null}
        <Button
          type="button"
          variant="primary"
          size="m"
          className="mt-8 mb-5 w-full"
          onClick={() => handleSubmit()}
          disabled={isDisabled()}
          loading={isLoading}
        >
          Verify
        </Button>
        <span className="flex flex-wrap justify-center gap-y-[5px] text-center text-sm font-medium text-[#4a5466] [&_a]:text-[#1453f5] [&_a]:no-underline">
          Didn’t receive an SMS?{" "}
          {timeLeft <= 0 ? (
            <StyledLink onClick={handleResend} to="">
              Re-send
            </StyledLink>
          ) : (
            `Re-send in ${formatTime(timeLeft)}`
          )}
        </span>
      </div>
      <Footer />
      <div id="recaptcha-container"></div>
    </DashboardLayout>
  );
};
